// 往来结算的状态文案与颜色（文档 18 §4：5 色 + 灰，一行只一个实底标签）。
// 口径全部照原系统代码：
// - 差额阈值 0.004（src/lib/settlements.ts matchBalance、SettlementPageClient getCashStatus / getInvoiceStatus）
// - 对账单四态（SettlementStatementStatus）与流转（src/lib/settlement-statements.ts updateSettlementStatementStatus）
import type { TagTone } from '@/lib/status';
import { orderStatus } from '@/lib/status';
import type { OrderStatus } from '@/api/types';
import type { Scope, StatementStatus } from './types';

export const EPS = 0.004;

/** 各维度的字段名（老系统 modeConfig），客户与供应商同一套卡片只换字段名 */
export const LABELS = {
  customer: {
    title: '客户往来',
    partner: '客户',
    bill: '订单应收',
    cash: '已收款',
    cashShort: '已收',
    open: '待收',
    openAmount: '待收金额',
    over: '预收',
    overAmount: '预收金额',
    invRequired: '应开票',
    inv: '已开票',
    invOpen: '待开票',
    invOpenAmount: '待开票金额',
    invOver: '超开票',
    invOverAmount: '超开票金额',
    invDone: '开票完成',
    cashRecord: '收款记录',
    invRecord: '开票记录',
    cashVerb: '收款',
    invVerb: '开票',
    cashGroup: '收款结算',
    invGroup: '开票结算',
    docs: '订单',
  },
  supplier: {
    title: '供应商往来',
    partner: '供应商',
    bill: '采购应付',
    cash: '已付款',
    cashShort: '已付',
    open: '待付',
    openAmount: '待付金额',
    over: '预付',
    overAmount: '预付金额',
    invRequired: '应收票',
    inv: '已收票',
    invOpen: '待收票',
    invOpenAmount: '待收票金额',
    invOver: '超收票',
    invOverAmount: '超收票金额',
    invDone: '收票完成',
    cashRecord: '付款记录',
    invRecord: '收票记录',
    cashVerb: '付款',
    invVerb: '收票',
    cashGroup: '付款结算',
    invGroup: '收票结算',
    docs: '采购单',
  },
} as const;
export type ScopeLabels = (typeof LABELS)[Scope];

/** 日期口径：决定哪一批业务单据落在这个月（老系统 dateOptions / dateBasisOptions），第一项是默认 */
export const DATE_BASIS: Record<Scope, { value: string; label: string }[]> = {
  customer: [
    { value: 'customerOrderDate', label: '客户下单日期' },
    { value: 'createdAt', label: '创建时间' },
    { value: 'deliveryDate', label: '交货期' },
  ],
  supplier: [
    { value: 'orderDate', label: '采购日期' },
    { value: 'expectedArrivalDate', label: '预计到货' },
    { value: 'createdAt', label: '创建时间' },
  ],
};
/** V2 默认口径与 T1/T2 原稿一致：客户下单日 / 采购日期；不改变老版默认。 */
export function basisLabel(v?: string | null, scope: Scope = 'customer') {
  if (!v) return DATE_BASIS[scope][0].label;
  return [...DATE_BASIS.customer, ...DATE_BASIS.supplier].find((o) => o.value === v)?.label || v;
}

export function agingTone(bucket?: string | null): TagTone | undefined {
  if (!bucket) return undefined;
  if (bucket === '未到期') return 'blue';
  if (bucket === '0–30 天' || bucket === '31–60 天') return 'amber';
  return 'red';
}

/** 资金状态：全卡唯一的实底标签。超收 / 超付用紫色，与「欠钱」分开（高保真稿 T1） */
export function cashState(balance: number | undefined, scope: Scope): { label: string; tone: TagTone } | null {
  if (balance === undefined) return null;
  const L = LABELS[scope];
  if (balance > EPS) return { label: L.open, tone: 'amber' };
  if (balance < -EPS) return { label: L.over, tone: 'violet' };
  return { label: '已结清', tone: 'green' };
}

/** 发票状态：次级状态，用色点 */
export function invoiceState(balance: number | undefined, scope: Scope): { label: string; dot: 'amber' | 'blue' | 'green' } | null {
  if (balance === undefined) return null;
  const L = LABELS[scope];
  if (balance > EPS) return { label: L.invOpen, dot: 'amber' };
  if (balance < -EPS) return { label: L.invOver, dot: 'blue' };
  return { label: L.invDone, dot: 'green' };
}

/** 视图 chips：与老接口 paymentStatus / invoiceStatus 的 open / settled / overpaid 同一判断 */
export type BalanceView = 'all' | 'open' | 'invOpen' | 'over' | 'invOver' | 'settled';
export function matchView(v: BalanceView, row: { balanceAmount?: number; invoiceBalanceAmount?: number; cashStatus?: string; invoiceStatus?: string }) {
  const b = row.balanceAmount ?? 0;
  const i = row.invoiceBalanceAmount ?? 0;
  switch (v) {
    case 'open': return row.cashStatus ? row.cashStatus === 'open' : b > EPS;
    case 'over': return row.cashStatus ? row.cashStatus === 'over' : b < -EPS;
    case 'settled': return row.cashStatus ? row.cashStatus === 'settled' : Math.abs(b) <= EPS;
    case 'invOpen': return row.invoiceStatus ? row.invoiceStatus === 'open' : i > EPS;
    case 'invOver': return row.invoiceStatus ? row.invoiceStatus === 'over' : i < -EPS;
    default: return true;
  }
}

/* ---------------- 对账单 ---------------- */
export const statementStatus: Record<StatementStatus, { label: string; tone: TagTone }> = {
  DRAFT: { label: '草稿', tone: 'none' },
  CONFIRMED: { label: '已确认', tone: 'blue' },
  LOCKED: { label: '已锁账', tone: 'green' },
  CANCELLED: { label: '已作废', tone: 'red' },
};
export const STATEMENT_FLOW: StatementStatus[] = ['DRAFT', 'CONFIRMED', 'LOCKED'];

/**
 * 当前这一步之后能做什么（照原系统）：
 * - 草稿 → 确认（settlements.statement.confirm）
 * - 已确认 → 锁账（settlements.statement.lock）
 * - V2 中已确认可反确认回草稿；已锁账可解锁回已确认
 */
export function statementNext(st: StatementStatus): { title: string; text: string } {
  switch (st) {
    case 'DRAFT':
      return { title: '下一步 确认对账单', text: '和对方核对过金额后确认；确认之后才能锁账。' };
    case 'CONFIRMED':
      return { title: '下一步 锁账', text: '锁账后作为结算依据；发现不对可以反确认退回草稿。' };
    case 'LOCKED':
      return { title: '已锁账', text: '已作为结算依据。需要修改时先解锁，退回已确认。' };
    default:
      return { title: '已作废', text: '这份对账单已作废，底层的业务单据、收付款和核销记录不受影响。' };
  }
}

/** 最近一次动作：卡片 / 表格的「最近动作」列 */
export function lastAction(s: { status: StatementStatus; createdAt: string; createdBy?: { name: string } | null; confirmedAt?: string | null; confirmedBy?: { name: string } | null; lockedAt?: string | null; lockedBy?: { name: string } | null; updatedAt: string }) {
  if (s.status === 'LOCKED' && s.lockedAt) return { who: s.lockedBy?.name, verb: '锁账', at: s.lockedAt };
  if (s.status === 'CONFIRMED' && s.confirmedAt) return { who: s.confirmedBy?.name, verb: '确认', at: s.confirmedAt };
  if (s.status === 'CANCELLED') return { who: undefined, verb: '作废', at: s.updatedAt };
  return { who: s.createdBy?.name, verb: '生成', at: s.createdAt };
}

/* ---------------- 业务单据状态（快照行存在 note 里，实时预览在 status 里） ---------------- */
const purchaseStatus: Record<string, { label: string; tone: TagTone }> = {
  DRAFT: { label: '草稿', tone: 'none' },
  ORDERED: { label: '待入库', tone: 'amber' },
  PARTIALLY_RECEIVED: { label: '部分入库', tone: 'orange' },
  RECEIVED: { label: '已入库', tone: 'green' },
  CANCELLED: { label: '已取消', tone: 'none' },
};
const taobaoStatus: Record<string, { label: string; tone: TagTone }> = {
  COMPLETED: { label: '已完成', tone: 'green' },
  PARTIALLY_RETURNED: { label: '部分退货', tone: 'orange' },
  RETURNED: { label: '已退货', tone: 'none' },
  CANCELLED: { label: '已取消', tone: 'none' },
};
export function documentStatus(targetType: string | undefined, code?: string | null): { label: string; tone: TagTone } | null {
  if (!code) return null;
  if (targetType === 'PURCHASE_ORDER') return purchaseStatus[code] || { label: code, tone: 'none' };
  if (targetType === 'TAOBAO_ORDER') return taobaoStatus[code] || { label: code, tone: 'none' };
  const o = orderStatus[code as OrderStatus];
  return o ? { label: o.label, tone: o.tone } : null;
}

/** 单据的开票口径：不计入应票的写「无需开票」 */
export function documentInvoice(d: { invoiceRequired: boolean; invoiceBalance?: number }, scope: Scope): { label: string; tone: TagTone } {
  const L = LABELS[scope];
  if (!d.invoiceRequired) return { label: scope === 'supplier' ? '无需收票' : '无需开票', tone: 'none' };
  if ((d.invoiceBalance ?? 0) > EPS) return { label: L.invOpen, tone: 'amber' };
  return { label: L.inv, tone: 'green' };
}
